import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { PawPrint } from "lucide-react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ComingSoonModal } from "@/components/ComingSoonModal";
import { NAV_ROOMS, SOCIAL_LINKS } from "@/data/doorways";
import { api } from "@/lib/api";

const EASE = [0.22, 1, 0.36, 1];

const Section = ({ label, children }) => (
  <motion.section
    initial={{ opacity: 0, y: 26 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.85, ease: EASE }}
    className="mt-14 sm:mt-16"
  >
    {label && (
      <h2 className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#C06A4E]">
        {label}
      </h2>
    )}
    <div className="mt-4 space-y-5 text-[15px] leading-[1.9] text-ink/75 sm:text-base">
      {children}
    </div>
  </motion.section>
);

export default function About() {
  const [navRoom, setNavRoom] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const subscribeNav = async (email) => {
    const { data } = await api.post("/subscribe", {
      email,
      interest: navRoom.name,
      source: navRoom.source,
    });
    return data;
  };

  const openSocial = (social) => {
    if (social.url) window.open(social.url, "_blank", "noopener,noreferrer");
    else toast(`${social.label} is coming soon`);
  };

  return (
    <div className="min-h-screen overflow-x-clip" data-testid="about-page">
      <Header onOpenNavRoom={(key) => setNavRoom(NAV_ROOMS[key])} />

      <main className="mx-auto w-full max-w-2xl px-6 pb-16">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: EASE }}
          className="text-center"
        >
          <div className="flex items-center justify-center gap-3">
            <PawPrint className="h-4 w-4 text-[#C06A4E]" fill="#C06A4E" strokeWidth={0} />
            <span className="text-[10px] font-semibold uppercase tracking-[0.32em] text-ink/55">
              Our story
            </span>
            <PawPrint className="h-4 w-4 text-[#C06A4E]" fill="#C06A4E" strokeWidth={0} />
          </div>
          <h1
            data-testid="about-title"
            className="mt-4 font-serif text-[34px] font-medium leading-tight tracking-tight text-ink sm:text-5xl"
          >
            About My Dachshund Days
          </h1>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, ease: EASE, delay: 0.15 }}
          className="mt-8 overflow-hidden rounded-[26px]"
        >
          <img
            src="/images/journey.jpg"
            alt="A dachshund sitting on a mountain overlook at golden hour beside an enamel mug"
            className="aspect-[16/8] w-full object-cover"
          />
        </motion.div>

        <Section>
          <p className="font-serif text-xl leading-[1.7] text-ink sm:text-2xl">
            My Dachshund Days is a creative world inspired by one very simple thing: we
            love dachshunds.
          </p>
          <p>
            We love their enormous personalities, their stubborn little opinions, their
            long bodies, their funny habits, their loyalty, and the way they somehow
            become part of nearly every moment of everyday life.
          </p>
          <p>That love is where My Dachshund Days began.</p>
        </Section>

        <Section label="How it started">
          <p>
            What started as a place to create dachshund-inspired art has grown into
            something much bigger.
          </p>
          <p>
            Today, My Dachshund Days is about dachshund life, art, stories, creativity,
            cozy moments, books, helpful resources, and a growing collection of ideas
            made for people who understand how special life with a dachshund can be.
          </p>
        </Section>

        <Section label="Why we create">
          <p>We love to create because there are so many ways to tell that story.</p>
          <p>Sometimes it becomes a piece of artwork.</p>
          <p>Sometimes it becomes stationery, a printable, a scrapbook idea, or a craft.</p>
          <p>Sometimes it becomes a children&rsquo;s book.</p>
          <p>Sometimes it becomes a quiet story about a dog sitting beside its person.</p>
          <p>
            Sometimes it becomes a strange woodworking idea that probably shouldn&rsquo;t
            work — but somehow absolutely needs to exist.
          </p>
          <p>
            And sometimes it is simply something beautiful, funny, or heartfelt that
            reminds us why these little dogs mean so much to us.
          </p>
        </Section>

        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 1, ease: EASE }}
          className="mt-14 overflow-hidden rounded-[26px] sm:mt-16"
        >
          <img
            src="/images/inspire.jpg"
            alt="A black and tan dachshund in a warm woodworking studio beside a wooden dachshund figure"
            className="aspect-[16/9] w-full object-cover"
            loading="lazy"
          />
        </motion.div>

        <Section label="A world bigger than one category">
          <p>We don&rsquo;t believe My Dachshund Days has to fit inside one creative category.</p>
          <p>The dachshund is the connection. Everything else is allowed to grow around it.</p>
          <p>That is why you will find many different kinds of things here:</p>
          <p className="font-serif text-lg italic leading-[2.2] text-ink/85 sm:text-xl">
            Art. Stories. Creative ideas. Books. Free resources. Crafts. Cozy moments.
            Inspiration. And plenty of things we haven&rsquo;t thought of yet.
          </p>
        </Section>

        <Section label="Life with dachshunds is worth celebrating">
          <p className="font-serif text-xl leading-[1.7] text-ink sm:text-2xl">
            At the heart of all of it is the same idea: life with dachshunds is worth
            celebrating.
          </p>
          <p>
            Whether you came here because you love dachshunds, found one of our creations
            on Pinterest, are looking for something to make, want something to read, or
            simply enjoy seeing what these long little dogs get into next, we&rsquo;re
            glad you&rsquo;re here.
          </p>
          <p>My Dachshund Days is still growing. And that&rsquo;s part of the fun.</p>
          <p className="font-serif text-lg italic text-ink/60">
            Little dogs. Big inspiration.
          </p>
        </Section>

        {/* Closing */}
        <motion.section
          initial={{ opacity: 0, y: 26 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.85, ease: EASE }}
          className="mt-16 flex flex-col items-center rounded-[28px] bg-white/60 px-6 py-12 text-center sm:mt-20 sm:px-10"
          data-testid="about-closing"
        >
          <img
            src="/images/brand-badge.jpg"
            alt="My Dachshund Days — two dachshunds among wildflowers"
            className="h-32 w-32 rounded-full object-cover shadow-[0_16px_40px_-18px_rgba(42,37,30,0.35)] sm:h-40 sm:w-40"
            loading="lazy"
          />
          <h2 className="mt-7 font-serif text-2xl font-medium tracking-tight text-ink sm:text-3xl">
            Thanks for being here
          </h2>
          <div className="mt-4 max-w-md space-y-4 text-[15px] leading-[1.85] text-ink/75">
            <p>
              We&rsquo;re building My Dachshund Days one idea, one story, and one
              dachshund at a time.
            </p>
            <p>
              We hope you find something here that makes you smile, inspires you to
              create, or reminds you of a dachshund you love.
            </p>
          </div>
        </motion.section>

        {/* Socials */}
        <motion.section
          initial={{ opacity: 0, y: 26 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.85, ease: EASE }}
          className="mt-14 text-center"
          data-testid="about-socials"
        >
          <h2 className="text-[10px] font-semibold uppercase tracking-[0.3em] text-ink/55">
            Follow My Dachshund Days
          </h2>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
            {SOCIAL_LINKS.map((social) => (
              <button
                key={social.id}
                type="button"
                data-testid={`social-${social.id}`}
                onClick={() => openSocial(social)}
                className="inline-flex min-h-[48px] items-center rounded-full border border-ink/15 bg-white/70 px-7 text-[12px] font-semibold uppercase tracking-[0.16em] text-ink/75 transition-colors duration-300 hover:border-[#C06A4E]/60 hover:text-[#C06A4E]"
              >
                {social.label}
              </button>
            ))}
          </div>
        </motion.section>
      </main>

      <Footer />

      <ComingSoonModal
        room={navRoom}
        open={Boolean(navRoom)}
        onClose={() => setNavRoom(null)}
        onSubscribe={subscribeNav}
      />
    </div>
  );
}
